import {
  Button,
  Checkbox,
  FormControl,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { SelectLight } from "../../../../common/components/styled/styled";
import { VerticalAlignGrid } from "../../../invoices/styled";
import { ReactComponent as EditIcon } from "../../../../common/icons/editGray.svg";
import { ReactComponent as ViewIcon } from "../../../../common/icons/viewGray.svg";
import { ReactNode } from "react";

interface Props {
  label: string;
  labelWidth?: number;
  fieldName: string;
  onSelectChange?: (newValue?: any) => void;
  optionsType?: "string" | "object";
  options?: string[];
  objectOptions?: { id: string; displayValue: string }[];
  multiple?: boolean;
  multipleEmptyValue?: string | null;
  disabled?: boolean;
  labelColor?: string;
  selectWidth?: number;
  getDisplayValue?: (val: string) => string;
  showTooltip?: boolean;
  tooltipText?: string | null;
  showButton?: boolean;
  showViewButton?: boolean;
  buttonError?: boolean;
  editButtonText?: string | null;
  viewButtonText?: string | null;
  onButtonClick?: () => void;
  selectError?: boolean;
  required?: boolean;
  validate?: (value: any) => boolean | string | Promise<boolean | string>;
}

const DEFAULT_LABEL_WIDTH = 3;
const DEFAULT_OPTIONS_TYPE = "string";
const DEFAULT_SELECT_WIDTH = 5;

const LabelSelectRow = ({
  label,
  labelWidth = DEFAULT_LABEL_WIDTH,
  fieldName,
  onSelectChange,
  optionsType = DEFAULT_OPTIONS_TYPE,
  options,
  objectOptions,
  multiple,
  multipleEmptyValue,
  disabled,
  labelColor,
  selectWidth = DEFAULT_SELECT_WIDTH,
  getDisplayValue,
  showTooltip,
  tooltipText,
  showButton,
  showViewButton = false,
  buttonError,
  editButtonText,
  viewButtonText,
  onButtonClick,
  selectError,
  required,
  validate,
}: Props) => {
  const { control } = useFormContext();

  const handleDisplayValue = (val: any) => {
    return getDisplayValue ? getDisplayValue(val) : val;
  };

  const handleRender = (val: any) => {
    if (Array.isArray(val)) {
      if (val.length === 0) return multipleEmptyValue;
      return val.map((v) => handleDisplayValue(v)).join(", ");
    }
    return handleDisplayValue(val);
  };

  const rules = {
    ...(required && { required }),
    validate: (value: Date) =>
      validate && typeof validate === "function" && validate(value),
  };

  const showStringOptions =
    options && options.length > 0 && optionsType === "string";
  const showObjectOptions =
    objectOptions && objectOptions.length > 0 && optionsType === "object";

  return (
    <VerticalAlignGrid container spacing={1}>
      <Grid
        container
        item
        xs={labelWidth}
        justifyContent="flex-end"
        paddingRight="0.5rem"
        textAlign="end"
      >
        <Typography variant="secondary" color={labelColor}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={selectWidth}>
        <FormControl fullWidth size="small">
          <Controller
            name={fieldName}
            control={control}
            rules={rules}
            render={({ field: { value, onChange } }) => (
              <SelectLight
                labelId="label"
                onChange={(
                  event: SelectChangeEvent<unknown>,
                  child: ReactNode,
                ) => {
                  onChange(event, child);
                  !!onSelectChange && onSelectChange(event.target.value);
                }}
                value={value}
                multiple={multiple}
                disabled={disabled}
                renderValue={(val) => handleRender(val)}
                displayEmpty
                error={selectError}
              >
                {showStringOptions &&
                  options.map((item) => (
                    <MenuItem key={item} value={item} dense>
                      {multiple && (
                        <Checkbox checked={value.indexOf(item) > -1} />
                      )}
                      {handleDisplayValue(item)}
                    </MenuItem>
                  ))}
                {showObjectOptions &&
                  objectOptions.map(({ id, displayValue }) => (
                    <MenuItem key={id} value={id} dense>
                      {multiple && (
                        <Checkbox checked={value.indexOf(id) > -1} />
                      )}
                      {displayValue}
                    </MenuItem>
                  ))}
              </SelectLight>
            )}
          />
        </FormControl>
      </Grid>
      {showTooltip && (
        <Grid item sx={{ marginLeft: "0.5rem" }}>
          <InfoTooltip
            title={tooltipText || ""}
            slotProps={{ popper: { sx: { whiteSpace: "pre-line" } } }}
          />
        </Grid>
      )}
      {showButton && (
        <Grid item>
          <Button
            variant="text"
            startIcon={showViewButton ? <ViewIcon /> : <EditIcon />}
            onClick={onButtonClick}
          >
            <Typography
              color={buttonError ? "error" : "textSecondary"}
              fontSize="14px"
            >
              {showViewButton ? viewButtonText : editButtonText}
            </Typography>
          </Button>
        </Grid>
      )}
    </VerticalAlignGrid>
  );
};

export default LabelSelectRow;
